import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EMessageType, ResponseDefault } from 'src/app/shared/data/response-default.interface';
import { BaseService } from 'src/app/shared/services/base.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { environment } from 'src/environments/environment';
import {
  IDiagnosticsRiskView,
  IListByFilterPatient,
  IMedicalRecord,
  IPatient,
  RiskClassification
} from '../models/patient.interface';

@Injectable({
  providedIn: 'root'
})
export class PatientService extends BaseService {
  private readonly apiUrl = `${this.urlService}${environment.microservices.doctor}DiagnosticsRisk`;

  private selectedPatient?: IPatient;

  constructor(
    protected readonly toastService: ToastService,
    private readonly http: HttpClient
  ) {
    super(toastService);
  }

  setSelectedPatient(patient: IPatient): void {
    this.selectedPatient = patient;
  }

  getSelectedPatient(): IPatient | undefined {
    return this.selectedPatient;
  }

  listarPacientes(filtro: IListByFilterPatient): Observable<ResponseDefault<IPatient[]>> {
    const params = this.buildParams(filtro)
      .set('pageNumber', String(filtro.pageNumber))
      .set('pageSize', String(filtro.pageSize));

    return this.http.get<ResponseDefault<IDiagnosticsRiskView[]>>(`${this.apiUrl}/List`, { params }).pipe(
      map((response) => ({
        ...response,
        data: response.data.map((item) => this.mapDiagnosticsToPatient(item))
      }))
    );
  }

  /** Busca o relatório pela sessão (F5 / link direto no prontuário). `undefined` quando a API não encontra. */
  buscarPaciente(sessionId: string): Observable<IPatient | undefined> {
    return this.http
      .get<ResponseDefault<IDiagnosticsRiskView>>(`${this.apiUrl}/${encodeURIComponent(sessionId)}`)
      .pipe(map((response) => (response.data ? this.mapDiagnosticsToPatient(response.data) : undefined)));
  }

  exportarCsv(filtro: IListByFilterPatient): void {
    const params = this.buildParams(filtro);

    this.http.get(`${this.apiUrl}/Export`, { params, responseType: 'blob', observe: 'response' }).subscribe({
      next: (response) => {
        const blob = response.body as Blob;

        const contentDisposition = response.headers.get('content-disposition');
        let fileName = 'RelatorioRisco.csv';

        if (contentDisposition) {
          const matches = /filename=\"?([^\";]+)\"?/i.exec(contentDisposition);
          if (matches && matches[1]) {
            fileName = matches[1];
          }
        }

        saveAs.saveAs(blob, fileName);
      },
      error: () =>
        this.toastService.toast('Não foi possível exportar o relatório. Tente novamente.', EMessageType.Error)
    });
  }

  // listagem e export usam os mesmos filtros; o OrderByEnum da API tem os mesmos valores do EOrdenacao.
  private buildParams(filtro: IListByFilterPatient): HttpParams {
    let params = new HttpParams().set('orderBy', String(filtro.orderBy));

    if (filtro.searchText) {
      params = params.set('searchText', filtro.searchText);
    }

    return params;
  }

  private mapDiagnosticsToPatient(item: IDiagnosticsRiskView): IPatient {
    const medicalRecord: IMedicalRecord = {
      symptoms: item.symptoms,
      evidences: item.evidences,
      conds: item.diagnosis,

      SystolicPressure: item.systolicPressure,
      DiastolicPressure: item.diastolicPressure,
      OxygenSaturation: item.oxygenSaturation,
      BreathingRate: item.breathingRate,
      HeartRate: item.heartRate,
      Temperature: item.temperature,

      Height: item.height,
      Weight: item.weight,
      IMC: item.imc,

      Overweight: item.overweight,
      Diabetes: item.diabetes,
      HighCholesterol: item.highCholesterol,
      Hypertension: item.hypertension,
      SmokerForOverTenYears: item.smokerForOverTenYears
    };

    return {
      id: item.sessionId,
      name: item.patientName,
      birthDate: item.birthDate,
      document: item.document,
      createdAt: item.createdAt,
      riskClassification: this.mapRiskSeverityToClassification(item.riskSeverity),
      medicalRecord
    };
  }

  private mapRiskSeverityToClassification(riskSeverity: string): RiskClassification {
    if (!riskSeverity) {
      return 'Não classificado';
    }

    const normalized = riskSeverity.toLowerCase();

    if (normalized.includes('low') || normalized.includes('baixo') || normalized.includes('mild')) {
      return 'Normal';
    }

    // a view devolve ALTO | MODERADO | BAIXO | unknown
    if (normalized.includes('medium') || normalized.includes('médio') || normalized.includes('modera')) {
      return 'Médio';
    }

    if (normalized.includes('high') || normalized.includes('alto') || normalized.includes('severe')) {
      return 'Alto';
    }

    return 'Não classificado';
  }
}
